import type { ReactNode } from 'react'
import { EditorTemplate } from './components/Editor/Editor'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { MainAttachments } from '@/stdDesk/Page/components/Attachments/MainAttachments'
import { MainMessage } from './components/MainMessage/MainMessage'
import { legacyContractAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { AnswersPanel, MenuSubsPanels, MessagesPanel, SubsToolbar } from '@/deskUiSkeletons/Page/Panels'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'

// Swappable content area inside the main workspace region (legacy e52 + e501-e505).
// The read view holds main message + toolbar + both panels; the other views
// are empty regions legacy fills directly in the DOM.

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; extraClass?: string; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const info = workspaceByKind[kind]
  // S1: workspace visibility must be in the SSR markup (pre-boot there is no
  // imperative chrome projection yet). chromeByContract is seeded per route by
  // payloadToState and is the SAME source projectChromeState applies post-boot,
  // so React and the imperative projector always agree. `hidden` + inline
  // display:none mirror exactly what projectChromeState writes for a hidden
  // contract; when visible, the markup stays untouched (also as before).
  const { state } = useSubsStore()
  const visible = state.chromeByContract[info.contract]?.visible !== false
  const workspaceClass = `md-workspace md-workspace--${kind} flex min-w-0 flex-[0_0_auto] flex-col${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}${visible ? '' : ' hidden'}`
  return (
    // SKELETON: workspace contracts are legacy visibility/mount targets.
    // Move this hook with the corresponding workspace panel; do not delete it.
    <div
      {...legacyHookAttrs(info.contract, workspaceClass)}
      style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }}
      {...(visible ? {} : { 'data-md-react-display': 'none' })}
    >
      {children}
    </div>
  )
}

function MsgWorkspace({ isEditorOpen, editorMode }: { isEditorOpen?: boolean; editorMode?: EditorMode }) {
  return (
    <>
      <WorkspaceSection kind="main">
        <MainMessage />
        <SubsToolbar />
        <AnswersPanel />
        <MessagesPanel />
      </WorkspaceSection>
      <WorkspaceSection kind="editor">
        <EditorTemplate editorMode={isEditorOpen ? editorMode : ''} />
      </WorkspaceSection>
      <WorkspaceSection kind="attachments">
        <MainAttachments />
      </WorkspaceSection>
      <WorkspaceSection kind="favs">
        <MenuSubsPanels mode="favs" />
      </WorkspaceSection>
      <WorkspaceSection kind="trash">
        <MenuSubsPanels mode="trash" />
      </WorkspaceSection>
      <WorkspaceSection kind="drafts">
        <MenuSubsPanels mode="drafts" />
      </WorkspaceSection>
    </>
  )
}

function RightRail() {
  return (
    <div
      {...legacyHookAttrs('page.rightRail', 'md-page-right-rail !hidden min-h-full w-[140px] flex-[0_0_140px] bg-white text-center')}
    >
      <div className="md-page-right-rail__logo mt-2.5 text-center">
        <img {...legacyContractAttrs('page.backImage')} src="imgs/logosm.png" />
      </div>
      <div
        {...legacyContractAttrs('page.advert')}
        className="md-page-advert m-0 hidden h-[500px] bg-white text-center"
      >
        реклама
      </div>
    </div>
  )
}

function Footer() {
  return (
    <div {...legacyHookAttrs('page.footer', 'md-page-footer flex h-5 flex-[0_0_20px] items-stretch bg-[#3472a9] text-white')}>
      <div className="md-page-footer__inner clear-both m-[2px_14px_2px_3px] flex items-center">
        <span className="md-page-footer__text text-[10px] text-white">
          <a href="iegyc7j70k27">© Msgdesk.com, Nazarov's project</a>
        </span>
      </div>
    </div>
  )
}

export function PageFrame({ isEditorOpen, editorMode }: { isEditorOpen?: boolean; editorMode?: EditorMode }) {
  return (
    // SKELETON: page.root/page.table are the stable outer page addresses.
    // Designers own md-page* styling, but these hooks must remain addressable.
    <div {...legacyHookAttrs('page.root', 'md-page flex min-h-full w-full flex-col')}>
      <div
        {...legacyHookAttrs('page.table', 'md-page-table msg std_rich flex min-h-full w-full flex-col items-stretch bg-white ml-0 mt-0 text-[11.2px] font-[Arial,Tahoma,Verdana,sans-serif]')}
      >
        <div {...legacyHookAttrs('page.header', 'md-page-header-row flex min-h-[60px] flex-[0_0_60px]')}>
          <div {...legacyHookAttrs('page.headerCell', 'md-page-header-cell ml-0 mt-0 min-h-[60px] flex-1 bg-white')} />
        </div>
        <div className="md-page-main-row flex min-h-0 flex-1 items-stretch">
          <div {...legacyHookAttrs('page.accentCell', 'md-page-accent-cell w-1 flex-[0_0_4px] bg-[#3472a9]')}>
            <div className="md-page-accent-cell__line h-[15px] w-0.5" />
          </div>
          <div
            {...legacyHookAttrs('page.workspaceCell', 'md-page-workspace-cell ml-[5px] mr-0 flex h-full min-w-0 flex-1 flex-col bg-white pb-[5px] pl-0 pt-0 w-full')}
          >
            <MsgWorkspace isEditorOpen={isEditorOpen} editorMode={editorMode} />
          </div>
          <RightRail />
        </div>
        <Footer />
      </div>
    </div>
  )
}
